import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_ITEM } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { isBetween } from "@cervval.js/common/src/tools/number.tools";
import { MINUTES_IN_ONE_DAY, MINUTES_IN_ONE_HOUR, SECONDS_IN_ONE_DAY, SECONDS_IN_ONE_HOUR, SECONDS_IN_ONE_MINUTE, convertTimeInMinutesToDaysHoursMinutes, daysToMinutes, hoursToMinutes, minutesToSeconds, secondsToMinutes } from "@cervval.js/common/src/tools/time.tools";
import { NumberTools, TimeTools } from "@cervval.js/common/tools";
import { Picker, PickerOptions } from "../picker";
import { I18nType, i18n } from "./i18n/i18n";

export interface DurationPickerRange {

    /** In minutes, included in range */
    startDuration: number;

    /** In minutes, included in range */
    finishDuration: number;
}

export interface DurationPickerOptions extends PickerOptions {

    /** allow negative numbers for duration */
    allowNegative: boolean;

    /** Duration in minutes by default 0*/
    durationInMinutes: number;

    /** Use hours, minutes and days */
    useDays: boolean;

    /** Display duration header units */
    displayHeaderUnits: boolean;

    /** Display selector header units */
    displaySelectorHeaderUnits: boolean;

    /** Enable duration selectors for days, hours and minutes */
    enableDurationSelectors: boolean;

    /** Ranges of duration for selection */
    ranges?: DurationPickerRange[];

    /** Time picker will use seconds as value unit instead of minutes */
    useSeconds?: boolean;
}

export const defaultDurationPickerOptions: DurationPickerOptions = {

    /** allow negative numbers for duration */
    allowNegative: false,

    /** Duration in minutes by default 0*/
    durationInMinutes: 0,

    /** Use hours, minutes and days */
    useDays: false,

    /** Display duration header units */
    displayHeaderUnits: true,

    /** Display selector header units */
    displaySelectorHeaderUnits: true,

    /** Enable duration selectors for days, hours and minutes */
    enableDurationSelectors: true,

    /** @inheritdoc */
    disabled: false
};

/** Mode of grid-selector when user click on value */
type DurationPickerSelectorMode = "day" | "hour" | "min";

const ARROW_DISABLED = "arrow-disabled";
const SELECTOR_DISABLED = "selector-disabled";
const ENABLE_SELECTOR_CLASS = "selector-enable";

@CustomElement({
    name: "cjs-duration-picker",
    style: require("./durationPicker.scss"),
    template: require("./durationPicker.html"),
    i18n,
    elementCssClasses: [FLEX_ITEM]
})
export class CjsDurationPicker extends Picker<number, I18nType> {

    @TypedWebComponentOptions(defaultDurationPickerOptions)
    public override readonly options!: WebComponentOptions<DurationPickerOptions>;

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /** Duration in minutes picker value */
    protected override _value = 0;

    /**
     * The components of the DurationPicker
     */
    @TypedHtmlElements()
    private readonly _components!: {
        durationPickerContainer: HTMLElement,
        durationDisplay: HTMLElement,
        headerUnits: HTMLElement,
        daysHeader: HTMLElement,
        dayUpBtn: HTMLElement,
        dayDownBtn: HTMLElement,
        hrUpBtn: HTMLElement,
        hrDownBtn: HTMLElement,
        minUpBtn: HTMLElement,
        minDownBtn: HTMLElement,
        daysValue: HTMLElement,
        hoursValue: HTMLElement,
        minutesValue: HTMLElement,
        daysSelectorContainer: HTMLElement,
        hoursSelectorContainer: HTMLElement,
        minutesSelectorContainer: HTMLElement,
        daysSelectorUndo: HTMLElement,
        hoursSelectorUndo: HTMLElement,
        minutesSelectorUndo: HTMLElement,
        daysSelector: HTMLElement,
        hoursSelector: HTMLElement,
        minutesSelector: HTMLElement,
        daysSelectorLabel: HTMLDivElement,
        hoursSelectorLabel: HTMLDivElement,
        minutesSelectorLabel: HTMLDivElement,
        durationDisplayHoursLabel: HTMLDivElement,
        durationDisplayMinutesLabel: HTMLDivElement
    };

    constructor(options?: Partial<DurationPickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this._value = this.useMinUnit() ? defaultDurationPickerOptions.durationInMinutes : minutesToSeconds(defaultDurationPickerOptions.durationInMinutes);
    }

    /**
     * Initialize hour and minute list in each Popup and days if showDay is true
     * Add all event listener to manipulate the DurationPicker (up and down button, wheel, popupList, displayedDuration)
     * Then show display the current duration of the DurationPicker
     */
    public override init(): void {
        this._initListeners();
    }

    /** Init component listeners */
    private _initListeners(): void {
        this._components.hrUpBtn.onclick = () => this._incHr();
        this._components.hrDownBtn.onclick = () => this._downHr();
        this._components.minUpBtn.onclick = () => this._incMin();
        this._components.minDownBtn.onclick = () => this._downMin();

        // Display the default DurationPicker view when we clicked on undo of durationSelector
        this._components.hoursSelectorUndo.onclick = (e: Event) => this.resetDurationPickerView();
        this._components.minutesSelectorUndo.onclick = (e: Event) => this.resetDurationPickerView();
        this._components.daysSelectorUndo.onclick = (e: Event) => this.resetDurationPickerView();

        // Selection of hour/minute in selector
        this._components.hoursSelector.onclick = (event: MouseEvent) => this._onDurationSelectorClick(event, "hour" as DurationPickerSelectorMode);
        this._components.minutesSelector.onclick = (event: MouseEvent) => this._onDurationSelectorClick(event, "min" as DurationPickerSelectorMode);
        this._components.daysSelector.onclick = (event: MouseEvent) => this._onDurationSelectorClick(event, "day" as DurationPickerSelectorMode);

        // Change duration with mousewheel
        this._components.daysValue.onwheel = (event: WheelEvent) => this._wheelDay(event);
        this._components.hoursValue.onwheel = (event: WheelEvent) => this._wheelHr(event);
        this._components.minutesValue.onwheel = (event: WheelEvent) => this._wheelMinute(event);
    }

    /** Refresh component display */
    public override refresh(): void {
        super.refresh();

        if (!this.options.allowNegative && this._value < 0) {
            this._log?.error("Negative is not allowed but durationInMinutes is negative");
        }

        // Update value from duration attribute
        this._value = this.useMinUnit() ? this.options.durationInMinutes : minutesToSeconds(this.options.durationInMinutes);

        // Handle header units display
        toggleVisible(this._components.headerUnits, this.options.displayHeaderUnits, Display.FLEX);

        // Show popupList on click of displayed duration
        this._components.daysValue.onclick = e => { return this.options.enableDurationSelectors ? this._durationSelectorHandler("day", e) : null; };
        this._components.hoursValue.onclick = e => { return this.options.enableDurationSelectors ? this._durationSelectorHandler("hour", e) : null; };
        this._components.minutesValue.onclick = e => { return this.options.enableDurationSelectors ? this._durationSelectorHandler("min", e) : null; };

        // If enable-selector class is not present, add class
        toggleClass(this._components.daysValue, this.options.enableDurationSelectors, ENABLE_SELECTOR_CLASS);
        toggleClass(this._components.hoursValue, this.options.enableDurationSelectors, ENABLE_SELECTOR_CLASS);
        toggleClass(this._components.minutesValue, this.options.enableDurationSelectors, ENABLE_SELECTOR_CLASS);

        if (this.options.enableDurationSelectors) {

            // Initialize list of element of days,hours and minutes
            this._initDaysSelector();
            this._initHourSelector();
            this._initMinutesSelector();
        }

        // Change duration with arrow click
        this._components.dayUpBtn.onclick = () => { return this.options.useDays ? this._incDay() : null; };
        this._components.dayDownBtn.onclick = () => { return this.options.useDays ? this._downDay() : null; };

        toggleVisible(this._components.daysHeader, this.options.useDays, Display.FLEX);
        toggleVisible(this._components.dayUpBtn, this.options.useDays, Display.FLEX);
        toggleVisible(this._components.dayDownBtn, this.options.useDays, Display.FLEX);
        toggleVisible(this._components.daysValue, this.options.useDays, Display.FLEX);

        // Refresh behavior based on ranges
        this._refreshListenersAndDisplayFromRanges();

        // Refresh i18n labels
        this._refreshI18nLabels();

        // Show the current duration
        this._updateDisplayedDuration();

    }

    /**
     * @returns true if picker is currently using time in minutes unit
     */
    public useMinUnit(): boolean {
        return !Boolean(this.options.useSeconds);
    }

    /**
     * Setter durationInMinutes of the DurationPicker
     */
    public override setValue(value: number): void {
        this._value = value;
        this._updateDisplayedDuration();
    }


    /**
     * Init daysSelector with list of values (0 - 100)
     */
    private _initDaysSelector(): void {
        this._components.daysSelector.innerHTML = this._getListElementHTML(0, 101, 1);
        this._components.daysSelector.style.gridTemplateColumns = "1fr 1fr 1fr 1fr 1fr 1fr";
    }

    /**
     * Init hoursList with list of values depending on useDays option
     */
    private _initHourSelector(): void {
        this._components.hoursSelector.innerHTML = this.options.useDays ? this._getListElementHTML(0, 23, 1) : this._getListElementHTML(0, 201, 1);
        this._components.hoursSelector.style.gridTemplateColumns = "1fr 1fr 1fr 1fr 1fr 1fr";
    }

    /**
     * Init minutesSelector with list of values (0 - 59)
     */
    private _initMinutesSelector(): void {
        this._components.minutesSelector.innerHTML = this._getListElementHTML(0, 59, 1);
        this._components.minutesSelector.style.gridTemplateColumns = "1fr 1fr 1fr 1fr 1fr 1fr";
    }

    /**
     * Return the list of span from start to end with value equal to span index
     * @param start begin on the list
     * @param end end on the list
     * @param step the step of the list
     */
    private _getListElementHTML(start: number, end: number, step: number): string {
        let html = "";
        let i = start;
        while (i <= end) {
            html += "<div class='selector-element' value='" + i + "'> " + NumberTools.twoDigits(i) + " </div>";
            i += step;
        }
        return html;
    }


    /** Refresh display based on given ranges */
    private _refreshListenersAndDisplayFromRanges(): void {

        if (this.options.useDays) {

            // DAYS UP
            const isValueInDurationRangeDayUp = this._isDurationInRange(this._value + MINUTES_IN_ONE_DAY);
            toggleClass(this._components.dayUpBtn, !isValueInDurationRangeDayUp, ARROW_DISABLED);

            // DAYS DOWN
            const isValueInDurationRangeDayDown = this._isDurationInRange(this._value - MINUTES_IN_ONE_DAY);
            toggleClass(this._components.dayDownBtn, !isValueInDurationRangeDayDown, ARROW_DISABLED);

        }

        // HOURS UP
        const isValueInDurationRangeHrUp = this._isDurationInRange(this._value + MINUTES_IN_ONE_HOUR);
        toggleClass(this._components.hrUpBtn, !isValueInDurationRangeHrUp, ARROW_DISABLED);

        // HOURS DOWN
        const isValueInDurationRangeHrDown = this._isDurationInRange(this._value - MINUTES_IN_ONE_HOUR);
        toggleClass(this._components.hrDownBtn, !isValueInDurationRangeHrDown, ARROW_DISABLED);

        // MINUTES UP
        const isValueInDurationRangeMinUp = this._isDurationInRange(this._value + 1);
        toggleClass(this._components.minUpBtn, !isValueInDurationRangeMinUp, ARROW_DISABLED);

        // MINUTES DOWN
        const isValueInDurationRangeMinDown = this._isDurationInRange(this._value - 1);
        toggleClass(this._components.minDownBtn, !isValueInDurationRangeMinDown, ARROW_DISABLED);

        // If value of picker is 0 and not allowed in range, set to minimum range possible
        if (this._value === 0 && !this._isDurationInRange(this._value) && this.options.ranges) {

            const lowestRangeTime = this.options.ranges.reduce((prev, curr) => {
                return prev.startDuration < curr.startDuration ? prev : curr;
            });

            this.setValue(lowestRangeTime.startDuration);
        }
    }


    /**
     * Update the displayed duration of the DurationPicker 
     */
    private _updateDisplayedDuration(): void {
        let sign = 1;
        let roundFunction = Math.floor;
        if (this._value < 0) {
            sign = -1;
            roundFunction = Math.ceil;
        }

        const minutesToDisplay = Math.abs(roundFunction(this.useMinUnit() ? this._value : secondsToMinutes(this._value)) % TimeTools.MINUTES_IN_ONE_HOUR);
        let hoursToDisplay = roundFunction((this.useMinUnit() ? this._value : secondsToMinutes(this._value)) / TimeTools.MINUTES_IN_ONE_HOUR);

        if (this.options.useDays) {
            // update hours to display to correct value
            hoursToDisplay = hoursToDisplay % TimeTools.HOURS_IN_ONE_DAY;
            const dayToDisplay = roundFunction((this.useMinUnit() ? this._value : secondsToMinutes(this._value)) / TimeTools.MINUTES_IN_ONE_DAY);
            this._components.daysValue.innerHTML = NumberTools.twoDigits(dayToDisplay);
        }

        this._components.hoursValue.innerHTML = NumberTools.twoDigits(hoursToDisplay);
        this._components.minutesValue.innerHTML = NumberTools.twoDigits(sign * minutesToDisplay);

        this._refreshListenersAndDisplayFromRanges();

        // Dispatch change event
        this._dispatchOnPickerValueChange();

        // Update durationInmInutes attribute
        this.options.setWithoutRefresh({ durationInMinutes: this.useMinUnit() ? this._value : secondsToMinutes(this._value) });
    }

    /**
     * Hide all selectors and display the durationDisplay element
     */
    public resetDurationPickerView(): void {
        if (this.disabled) { return; }// Don't allow resetDurationPickerView callback if component is disabled

        this._components.durationPickerContainer.querySelector(".selected")?.classList.remove("selected");
        this._components.daysSelectorContainer.style.display = Display.NONE;
        this._components.hoursSelectorContainer.style.display = Display.NONE;
        this._components.minutesSelectorContainer.style.display = Display.NONE;
        this._components.durationDisplay.style.display = "inline-table";
    }

    /**
     * Depending to the mode (hr or minute), we get the value of the selectors element clicked and set durationInMinutes with the new displayed value
     * @param event 
     * @param mode the mode
     */
    private _onDurationSelectorClick(event: MouseEvent, mode: DurationPickerSelectorMode): void {
        if (this.disabled) { return; }// Don't allow durationSelectorClick callback if component is disabled

        const targetElement = event.target as Element;
        const valueSelected = Number(targetElement.getAttribute("value"));

        // Check if the selected duration is valid for ranges
        if (!this._isDurationInRange(this._getMinutesForRangeWithSelector(mode, valueSelected))) {
            return;
        }

        const daysHoursMinutesCurrentValue = convertTimeInMinutesToDaysHoursMinutes(this._value);

        if (mode === "day") {
            this._value = (this.options.useDays ? valueSelected * MINUTES_IN_ONE_DAY : 0) + daysHoursMinutesCurrentValue.hours * MINUTES_IN_ONE_HOUR + daysHoursMinutesCurrentValue.minutes;
        } if (mode === "hour") {
            this._value = (this.options.useDays ? daysHoursMinutesCurrentValue.days * MINUTES_IN_ONE_DAY : 0) + valueSelected * MINUTES_IN_ONE_HOUR + daysHoursMinutesCurrentValue.minutes;
        } else if (mode === "min") {
            this._value = (this.options.useDays ? daysHoursMinutesCurrentValue.days * MINUTES_IN_ONE_DAY : 0) + daysHoursMinutesCurrentValue.hours * MINUTES_IN_ONE_HOUR + valueSelected;
        }

        this.resetDurationPickerView();
        this._updateDisplayedDuration();
    }

    /**
     * Increment durationInMinutes by 1 day
     */
    private _incDay(): void {
        const valueToIncrement = this.useMinUnit() ? MINUTES_IN_ONE_DAY : SECONDS_IN_ONE_DAY;
        if (!this._isDurationInRange(this._value + valueToIncrement)) {
            return;
        }

        if (0 > this._value && this._value > -valueToIncrement) {
            // For better ergonomics when pass from negative to positive
            this._value *= -1;
        }
        else { this._value += valueToIncrement; }
        this._updateDisplayedDuration();
    }

    /**
     * Decrement durationInMinutes by 1 day we don"t allow negative durationInMinutes 
     * except if [[options.allowNegative]] is set to true
     */
    private _downDay(): void {
        const valueToDecrement = this.useMinUnit() ? MINUTES_IN_ONE_DAY : SECONDS_IN_ONE_DAY;
        if (!this._isDurationInRange(this._value - valueToDecrement)) {
            return;
        }

        if (this.options.allowNegative && this._value < valueToDecrement || this._value >= valueToDecrement) {
            if (0 < this._value && this._value < valueToDecrement) {
                // For better ergonomics when pass from negative to positive
                this._value *= -1;
            }
            else {
                this._value -= valueToDecrement;
            }
        }
        else if (!this.options.allowNegative && this._value < valueToDecrement) {
            // We are between 0 and 23 hours but we can"t go to negative number so we go to 0
            this._value = 0;
        }
        this._updateDisplayedDuration();
    }

    /**
     * Increment durationInMinutes by 1 hour, if useDays if we exceed 24 hr: we set durationInMinutes to x days & 00 hr & x minutes
     */
    private _incHr(): void {
        if (this.disabled) { return; }// Don't allow incHr callback if component is disabled

        const valueToIncrement = this.useMinUnit() ? MINUTES_IN_ONE_HOUR : SECONDS_IN_ONE_HOUR;
        if (!this._isDurationInRange(this._value + valueToIncrement)) {
            return;
        }

        if (0 > this._value && this._value > -valueToIncrement) {
            // For better ergonomics when pass from negative to positive
            this._value *= -1;
        }
        else { this._value += valueToIncrement; }
        this._updateDisplayedDuration();
    }

    /**
     * Decrement durationInMinutes by 1 hour, we don"t allow negative durationInMinutes 
     * except if [[options.allowNegative]] is set to true
     */
    private _downHr(): void {
        if (this.disabled) { return; }// Don't allow downhr callback if component is disabled

        const valueToDecrement = this.useMinUnit() ? MINUTES_IN_ONE_HOUR : SECONDS_IN_ONE_HOUR;
        if (!this._isDurationInRange(this._value - valueToDecrement)) {
            return;
        }

        if (this.options.allowNegative && this._value < valueToDecrement || this._value >= valueToDecrement) {
            if (0 < this._value && this._value < valueToDecrement) {
                // For better ergonomics when pass from negative to positive
                this._value *= -1;
            }
            else {
                this._value -= valueToDecrement;
            }
        }
        else if (!this.options.allowNegative && this._value < valueToDecrement) {
            // We are between 0 and 59 minutes but wen can go to negative number so we go to 0
            this._value = 0;
        }
        this._updateDisplayedDuration();
    }
    /**
     * Increment durationInMinutes by 1 minute
     */
    private _incMin(): void {
        if (this.disabled) { return; }// Don't allow incMin callback if component is disabled

        const valueToIncrement = this.useMinUnit() ? 1 : SECONDS_IN_ONE_MINUTE;
        if (!this._isDurationInRange(this._value + valueToIncrement)) {
            return;
        }

        this._value += valueToIncrement;
        this._updateDisplayedDuration();
    }

    /**
     * Decrement durationInMinutes by 1 minute, we don't allow negative durationInMinutes 
     * except if [[options.allowNegative]] is set to true
     */
    private _downMin(): void {
        if (this.disabled) { return; }// Don't allow downMin callback if component is disabled

        const valueToDecrement = this.useMinUnit() ? 1 : SECONDS_IN_ONE_MINUTE;
        if (!this._isDurationInRange(this._value - valueToDecrement)) {
            return;
        }

        if (this.options.allowNegative && this._value <= 0 || this._value > 0) {
            this._value -= valueToDecrement;
        }
        this._updateDisplayedDuration();
    }

    /**
     * Wheel Handler for hours
     */
    private _wheelDay(event: WheelEvent): void {
        if (this.disabled) { return; }// Don't allow wheelDay callback if component is disabled

        if (event.deltaY > 0) {
            // Wheel Down
            this._downDay();
        } else {
            // Wheel up
            this._incDay();
        }
    }

    /**
     * Wheel Handler for hours
     */
    private _wheelHr(event: WheelEvent): void {
        if (this.disabled) { return; }// Don't allow wheelHr callback if component is disabled

        if (event.deltaY > 0) {
            // Wheel Down
            this._downHr();
        } else {
            // Wheel up
            this._incHr();
        }
    }

    /**
     * Wheel Handler for minutes
     */
    private _wheelMinute(event: WheelEvent): void {
        if (this.disabled) { return; }// Don't allow wheelMinute callback if component is disabled

        if (event.deltaY > 0) {
            // Wheel Down
            this._downMin();
        } else {
            // Wheel up
            this._incMin();
        }
    }

    /**
     * Display the hours selector or minutes selector
     * Hide the durationDisplay element
     * @param selector the selector we want to display
     */
    private _durationSelectorHandler(selector: DurationPickerSelectorMode, event: MouseEvent): void {

        const targetElement = event.target as Element;
        const valueSelected = +targetElement.innerHTML;

        /** Toggle disable state of selectorContainers if needed */
        this._applyRangesToSelector(selector);

        let selectedElement: HTMLElement | null = null;

        if (selector === "day") {
            this._components.daysSelectorContainer.style.display = Display.BLOCK;
            selectedElement = this._components.daysSelectorContainer.querySelector("[value='" + valueSelected + "']");
        } else if (selector === "hour") {
            this._components.hoursSelectorContainer.style.display = Display.BLOCK;
            selectedElement = this._components.hoursSelectorContainer.querySelector("[value='" + valueSelected + "']");
        } else if (selector === "min") {
            this._components.minutesSelectorContainer.style.display = Display.BLOCK;
            selectedElement = this._components.minutesSelectorContainer.querySelector("[value='" + valueSelected + "']");
        }

        if (selectedElement) {
            selectedElement.classList.add("selected");
        }

        // If selector header hide if required in options
        const selectorHeaders = this._components.durationPickerContainer.querySelectorAll(".selector-header");
        if (selectorHeaders) {
            selectorHeaders.forEach((selector) => {
                toggleVisible(selector as HTMLElement, this.options.displaySelectorHeaderUnits, Display.FLEX);
            });
        }

        this._components.durationDisplay.style.display = Display.NONE;
    }

    /** @returns true if given duration is in one range for picker */
    private _isDurationInRange(duration: number | undefined): boolean {

        if (duration == null) { return false; }
        const durationInMinutes = this.useMinUnit() ? duration : secondsToMinutes(duration);
        // If no range given or empty, all range is enable
        if (!this.options.ranges || this.options.ranges.length === 0) { return true; }

        for (const range of this.options.ranges) {
            const startRange = this.useMinUnit() ? range.startDuration : secondsToMinutes(range.startDuration);
            const finishRange = this.useMinUnit() ? range.finishDuration : secondsToMinutes(range.finishDuration);
            if (isBetween(durationInMinutes, startRange, finishRange, true)) {
                return true;
            }
        }

        return false;
    }


    /** Apply ranges  */
    private _applyRangesToSelector(valueSelector: DurationPickerSelectorMode): void {
        let selectorContainer: HTMLElement;

        switch (valueSelector) {
            case "day":
                selectorContainer = this.options.useDays ? this._components.daysSelector : this._components.hoursSelector;
                break;
            case "hour":
                selectorContainer = this._components.hoursSelector;
                break;
            default:
                selectorContainer = this._components.minutesSelector;
        }

        const selectors = Array.from(selectorContainer.querySelectorAll(".selector-element")) as HTMLDivElement[];
        for (const selector of selectors) {
            const value = Number(selector.getAttribute("value"));
            toggleClass(selector, !this._isDurationInRange(this._getMinutesForRangeWithSelector(valueSelector, value)), SELECTOR_DISABLED);
        }
    }

    /**
     * Get duration in minutes, for current selector
     * ex: if selector = "days", value = 7, currentValue = 2d14h25 => return 7d14h25 
     * ex: if selector = "hour", value = 5, currentValue = 14h25 => return 5h25 
     * ex: if selector = "min", value = 36, currentValue = 8h51 => return 8h36 
     */
    private _getMinutesForRangeWithSelector(valueSelector: DurationPickerSelectorMode, value: number): number {
        let minutesOfRange = 0;
        let roundFunction = Math.floor;
        if (this._value < 0) {
            roundFunction = Math.ceil;
        }

        const currentValueParsed = convertTimeInMinutesToDaysHoursMinutes(this.useMinUnit() ? this._value : secondsToMinutes(this._value));

        if (valueSelector === "day") {
            minutesOfRange = (this.options.useDays ? daysToMinutes(value) : 0) + hoursToMinutes(currentValueParsed.hours) + roundFunction(currentValueParsed.minutes);
        }
        else if (valueSelector === "hour") {
            minutesOfRange = (this.options.useDays ? daysToMinutes(currentValueParsed.days) : 0) + hoursToMinutes(value) + roundFunction(currentValueParsed.minutes);
        }
        else if (valueSelector === "min") {
            minutesOfRange = (this.options.useDays ? daysToMinutes(currentValueParsed.days) : 0) + hoursToMinutes(currentValueParsed.hours) + roundFunction(value);
        }
        return minutesOfRange;
    }

    /** Refresh label of timePicker */
    private _refreshI18nLabels(): void {
        this._components.daysSelectorLabel.innerText = this.i18n.labels.days;
        this._components.hoursSelectorLabel.innerText = this.i18n.labels.hours;
        this._components.minutesSelectorLabel.innerText = this.i18n.labels.minutes;
        this._components.daysHeader.innerText = this.i18n.labels.days;
        this._components.durationDisplayHoursLabel.innerText = this.i18n.labels.hours;
        this._components.durationDisplayMinutesLabel.innerText = this.i18n.labels.minutes;
    }
}

